import React from "react"
import { DynamicHeader } from "./DynamicHeader"
import { Footer } from "./Footer"
import {
  SKIP_TO_CONTENT_ANCHOR_KEY,
  MAIN_NAV_CHILD_ANCHOR_KEY,
} from "../../constants"

export interface ContentLayoutComponentProps {
  brandComponent?: React.ReactNode
  topRightComponents?: React.ReactNode[] // key needs to be included in these components
  topRightItemAlignment?: "start" | "center" | "end"
  topRightXSpacing?: string
  menuComponent?: React.ReactNode
  menuComponentVisible?: boolean
  bottomRightComponent?: React.ReactNode // key needs to be included in these components
  bottomLeftComponent?: React.ReactNode // key needs to be included in these components
  footerVisible?: boolean
  contactLink: () => void
  onAboutClick?: () => void
  shouldShowAboutLink?: boolean
  legalPrivacyLink: string
  termsLink: string
  text: {
    contactText: string
    aboutText: string
    helpLinkText: string
    legalPrivacyText: string
    termsText: string
    homeLinkText: string
    messageLinkText: string
    menuText: string
    skipToMainContentText: string
  }
  children?: React.ReactNode
}

export const ContentLayoutComponent: React.FC<ContentLayoutComponentProps> = ({
  brandComponent,
  topRightComponents,
  topRightItemAlignment,
  topRightXSpacing,
  menuComponent,
  menuComponentVisible,
  bottomRightComponent,
  bottomLeftComponent,
  footerVisible = true,
  contactLink,
  onAboutClick,
  shouldShowAboutLink,
  legalPrivacyLink,
  termsLink,
  text,
  children,
}) => {
  const headerProps = {
    brandComponent,
    topRightComponents,
    topRightItemAlignment,
    topRightXSpacing,
    menuComponent,
    menuComponentVisible,
    bottomRightComponent,
    bottomLeftComponent,
  }

  const footerProps = {
    contactLink,
    onAboutClick,
    shouldShowAboutLink,
    legalPrivacyLink,
    termsLink,
    footerVisible,
    text: {
      contactText: text.contactText,
      aboutText: text.aboutText,
      legalAndPrivacyText: text.legalPrivacyText,
      termsAndConditionsText: text.termsText,
    },
  }

  const handleSkipClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // prefer main content form first element if present
    const formElementAnchor = document.querySelector(
      `[${SKIP_TO_CONTENT_ANCHOR_KEY}=true]`,
    ) as HTMLElement | null
    // secondary the main nav child anchor
    const mainNavChildAnchor = document.getElementById(
      MAIN_NAV_CHILD_ANCHOR_KEY,
    ) as HTMLElement | null
    const main = document.getElementById("main-content") as HTMLElement | null

    // Prevent the default hash navigation and handle focus programmatically
    e.preventDefault()

    const target = formElementAnchor || mainNavChildAnchor || main
    if (target) {
      // focus the element (it has tabIndex=-1)
      target.focus()
      // Optionally ensure it's fully visible if sticky header covers it
      target.scrollIntoView({ block: "center", behavior: "smooth" })
    } else {
      // fallback for odd cases - let the browser navigate to #main-content
      window.location.hash = "#main-content"
    }
  }

  return (
    <div className="flex flex-col min-h-screen min-w-screen bg-secondary-grey relative">
      {/* Skip link for keyboard users (visible on focus) */}
      <a
        href="#main-content"
        onClick={handleSkipClick}
        className={
          "absolute -left-[999px] top-auto w-px h-px overflow-hidden whitespace-nowrap " +
          "focus:fixed focus:left-3 focus:top-3 focus:w-auto focus:h-auto focus:px-3 focus:py-2 " +
          "focus:bg-black focus:text-white focus:rounded focus:z-50"
        }
      >
        {text.skipToMainContentText}
      </a>

      {/* Static (site-wide) header */}
      <DynamicHeader {...headerProps} />

      {/* Main */}
      <main
        id="main-content"
        tabIndex={-1}
        className="h-full w-full flex flex-col flex-grow"
        role="main"
      >
        {children}
      </main>

      {/* Static footer */}
      {footerVisible && <Footer {...footerProps} />}
    </div>
  )
}
